import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "../ui/Button.tsx";
import s from "./shell/shell.module.css";

/** WHERE the boundary sits. A CLOSED SET, because it is a metric label (`web_render_errors_total{boundary}`)
 *  and every label value in this app comes from a union the server validates against. */
export type Boundary = "root" | "route" | "panel";

interface Props {
  boundary: Boundary;
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/** THREE boundaries, not one: root, per route, and one around the trade panel.
 *
 *  With none, a single throw blanks the whole page. Three is the minimum that keeps the
 *  failures independent — a throw inside the trade panel must not take the chart with it, nor the reverse, and
 *  a user staring at a blank page cannot even tell us which screen they were on. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only. The message is FREE TEXT and free text never reaches telemetry: `obs/metrics.ts` will count
    // this by `boundary`, which is a closed label, and the text stays in the browser where it cannot create an
    // unbounded time series or carry an address into our metrics store.
    console.error(
      `[web] render error in ${this.props.boundary} boundary`,
      error,
      info.componentStack,
    );
    // TODO(obs, Phase I): countRenderError(this.props.boundary) — `web_render_errors_total{boundary}`.
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className={s.errorBox} role="alert">
        <p>
          Something broke while rendering this {this.props.boundary === "root" ? "page" : "section"}
          .
        </p>
        {/* Retry re-mounts the subtree rather than reloading: a reload on the root boundary would discard a
            typed amount, and on the panel boundary it would discard an in-flight order's on-screen state. */}
        <Button variant="primary" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    );
  }
}
